import { inject, Injectable } from '@angular/core';
import { DeviceClientService } from '@services/api/device/device-client.service';
import { IDeviceModel } from '@services/api/device/device-model.interface';
import { HardCodedConfigService } from '@services/util/hard-coded-config.service';
import { LogService } from '@services/util/log.service';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  ArrivalBookingRow,
  ConfirmableBookingRow,
  DepartureBookingRow,
  GuestBookingRow
} from '../models/booking-overview.model';
import { EMPTY_FLAT_INDEX, FlatIndex, indexByFlat } from './flat-index';
import { presenceFlatIds, presenceStatusForFlats } from './presence-rules';

export type PresenceRow = ConfirmableBookingRow | ArrivalBookingRow | GuestBookingRow | DepartureBookingRow;
export type PresenceIndex = FlatIndex<IDeviceModel>;

/**
 * Jelenlét a foglalások áttekintéséhez: egyetlen összevont lekérés az összes jelenlét-érzékelőre,
 * lakásra indexelve; a sorok ebből kapják az állapotot (spec 6.3).
 */
@Injectable({
  providedIn: 'root'
})
export class BookingPresenceService {
  private readonly deviceClientService = inject(DeviceClientService);
  private readonly config = inject(HardCodedConfigService);
  private readonly log = inject(LogService);

  /** Az összes jelenlét-érzékelő lakásra indexelve; hibánál üres index (mintha minden lakásonkénti kérés elbukott volna). */
  loadPresenceIndex(): Observable<PresenceIndex> {
    return this.deviceClientService.getDevices({ 'deviceType.id': this.config.DEVICE_TYPE_PRESENCE_DETECTOR_ID }).pipe(
      map((devices) => indexByFlat(devices, (device) => device.flat?.id)),
      catchError((error: unknown) => {
        this.log.error('Failed to load presence devices', error);
        return of(EMPTY_FLAT_INDEX);
      })
    );
  }

  markPresenceLoading(row: PresenceRow): void {
    row.presenceLoading = true;
  }

  /** A sor összes lakásának eszközeiből összesített állapot (bármelyik jelez → foglalt). */
  applyPresence(row: PresenceRow, index: PresenceIndex): void {
    row.presenceStatus = presenceStatusForFlats(presenceFlatIds(row.flatInfos), index, new Date());
    row.presenceLoading = false;
  }
}
